import { useAppDispatch } from '@/app/redux/hooks';
import { setContactFormModalOpen } from '@/app/redux/slices/modalsSlice';
import { BillingInfo } from '@/common/types/organisation';
import tw from '@/common/utils/twmacro';
import addressFormatter from '@fragaria/address-formatter';
import { Skeleton } from '@shadcn/components/skeleton';
import { Address } from '@stripe/stripe-js';

interface BillingDetailsProps {
  billingInfo: BillingInfo | null;
  loading: boolean;
}
export default function BillingDetails({
  billingInfo,
  loading,
}: BillingDetailsProps) {
  const dispatch = useAppDispatch();

  function getAddressLines(address: Address) {
    const a = Object.entries(address).reduce(
      (acc: Record<string, string | undefined>, [key, value]) => {
        if (key === 'country') {
          acc.countryCode = value;
          return acc;
        }
        if (value) {
          acc[key] = value;
        } else {
          acc[key] = undefined;
        }
        return acc;
      },
      {} as Record<string, string | undefined>,
    );
    return addressFormatter.format(a, { output: 'array', appendCountry: true });
  }

  return loading ? (
    <BillingInfoContainer>
      <BillingInfoSection>
        <BillingInfoRow>
          <span className="font-medium">Name</span>
          <Skeleton className="h-6 w-full" />
        </BillingInfoRow>
        <BillingInfoRow>
          <span className="font-medium">Email</span>
          <Skeleton className="h-6 w-full" />
        </BillingInfoRow>
        <BillingInfoRow>
          <span className="font-medium">Phone</span>
          <Skeleton className="h-6 w-full" />
        </BillingInfoRow>
      </BillingInfoSection>
      <BillingInfoSection>
        <BillingInfoRow>
          <span className="font-medium">Address</span>
          <span className="text-muted-foreground flex flex-col gap-1">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </span>
        </BillingInfoRow>
      </BillingInfoSection>
      <BillingInfoRow>
        <span className="font-medium">Payment Method</span>
        <Skeleton className="h-6 w-full" />
      </BillingInfoRow>
    </BillingInfoContainer>
  ) : billingInfo ? (
    <BillingInfoContainer>
      <BillingInfoSection>
        <BillingInfoRow>
          <span className="font-medium">Name</span>
          <span className="text-muted-foreground">{billingInfo.name}</span>
        </BillingInfoRow>
        <BillingInfoRow>
          <span className="font-medium">Email</span>
          <span className="text-muted-foreground">{billingInfo.email}</span>
        </BillingInfoRow>
        {billingInfo.phone && (
          <BillingInfoRow>
            <span className="font-medium">Phone</span>
            <span className="text-muted-foreground">{billingInfo.phone}</span>
          </BillingInfoRow>
        )}
      </BillingInfoSection>
      <BillingInfoSection>
        <BillingInfoRow>
          <span className="font-medium">Address</span>
          <span className="text-muted-foreground flex flex-col">
            {getAddressLines(billingInfo.address).map((line, index) => (
              <span key={index}>{line}</span>
            ))}
          </span>
        </BillingInfoRow>
      </BillingInfoSection>
      <BillingInfoRow>
        <span className="font-medium">Payment Method</span>
        <span className="text-muted-foreground">
          {billingInfo.card
            ? `${billingInfo.card.brand} •••• ${billingInfo.card.last4}`
            : 'Invoice'}
        </span>
      </BillingInfoRow>
    </BillingInfoContainer>
  ) : (
    <div className="flex flex-col gap-2">
      <span className="text-destructive">
        Oops, we couldn't find your billing information! Please{' '}
        <span
          className="cursor-pointer underline"
          onClick={() => {
            dispatch(setContactFormModalOpen(true));
          }}
        >
          contact support
        </span>{' '}
        immediately.
      </span>
    </div>
  );
}

const BillingInfoContainer = tw.div`flex flex-col sm:grid sm:grid-cols-2 gap-4`;
const BillingInfoSection = tw.div`flex flex-col gap-1`;
const BillingInfoRow = tw.div`flex flex-col text-sm`;
